/**
 * useMoverSidecar — same shape as useModuleSidecar but for movers.
 *
 * Lookup order: built-in compiled sidecar → HTTP fetch → identity. See the
 * useModuleSidecar comment for the rationale.
 */

import { useEffect, useState } from 'react';
import {
  SidecarLoader,
  type MoverSidecar,
  type OriginCorrection,
  DEFAULT_ORIGIN_CORRECTION,
  moverSidecarFilename,
} from '../../assets/SidecarLoader.js';
import { BUILTIN_MOVER_SIDECARS } from '../../assets/builtinSidecars.js';
import { useSidecarSource } from '../../assets/SidecarSourceContext.js';
import { composeAssetUrl } from '../../assets/AssetLoader.js';
import type { MoverType3D } from '../../types.js';

const SHARED = new SidecarLoader();

export function useMoverSidecar(
  moverType: MoverType3D,
  assetsBaseUrl: string,
): { sidecar: MoverSidecar | undefined; correction: OriginCorrection } {
  const { useBuiltins } = useSidecarSource();
  const builtin =
    moverType === 'Custom' || !useBuiltins
      ? undefined
      : BUILTIN_MOVER_SIDECARS[moverType];
  const [sidecar, setSidecar] = useState<MoverSidecar | undefined>(builtin);

  useEffect(() => {
    if (moverType === 'Custom') {
      // Custom movers carry their layout via XPU.customMoverLayout; no
      // sidecar lookup makes sense.
      setSidecar(undefined);
      return;
    }
    if (builtin) {
      setSidecar(builtin);
      return;
    }
    let alive = true;
    const url = composeAssetUrl(
      assetsBaseUrl,
      moverSidecarFilename(moverType),
    );
    SHARED.fetchMover(url)
      .then((s) => {
        if (alive) setSidecar(s);
      })
      .catch(() => {
        if (alive) setSidecar(undefined);
      });
    return () => {
      alive = false;
    };
  }, [moverType, assetsBaseUrl, builtin]);

  return {
    sidecar,
    correction: sidecar?.originCorrection ?? DEFAULT_ORIGIN_CORRECTION,
  };
}
